// Quick add for the "Complete the look" row (blocks/sivana-pdp-goes-with.liquid): pick a size in a small
// dialog and add to the bag without leaving the page. Announces the add with the same Shopify cart event
// Horizon's own Add to Bag uses, so the cart drawer and cart count update and the drawer opens.
import { CartLinesUpdateEvent } from '@shopify/events';

const dialog = document.querySelector('[data-sivana-quick-add-dialog]');

if (dialog && !dialog.dataset.ready) {
  dialog.dataset.ready = 'true';
  const refs = {
    image: dialog.querySelector('[data-image]'),
    title: dialog.querySelector('[data-title]'),
    price: dialog.querySelector('[data-price]'),
    sizes: dialog.querySelector('[data-sizes]'),
    submit: dialog.querySelector('[data-submit]'),
    error: dialog.querySelector('[data-error]'),
    link: dialog.querySelector('[data-link]'),
  };
  let chosen = null;
  let productId = null;
  const root = window.Shopify?.routes?.root || '/';

  const colorsWrap = dialog.querySelector('[data-colors-wrap]');
  const colorRow = dialog.querySelector('[data-colors]');
  const colorName = dialog.querySelector('[data-color-name]');
  const sizeLabel = dialog.querySelector('[data-size-label]');

  let busy = false;
  const setSubmit = () => {
    // A new pick stops any Adding / Added animation still running on the button (assets/sivana-atc.js)
    refs.submit.svAtcRun = null;
    delete refs.submit.dataset.svAtc;
    refs.submit.disabled = !chosen;
    refs.submit.textContent = chosen ? 'Add to bag' : 'Select a size';
  };

  // Size buttons for one color (or for every variant when the product has no color option).
  const renderSizes = (data, color, keepLabel) => {
    const variants = data.variants.filter((variant) => !color || variant.color === color);
    refs.sizes.innerHTML = '';
    chosen = null;
    variants.forEach((variant) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sivana-quick-add__size';
      button.textContent = variant.label;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.disabled = !variant.available;
      button.addEventListener('click', () => {
        chosen = variant;
        refs.sizes.querySelectorAll('[role="radio"]').forEach((other) => other.setAttribute('aria-checked', String(other === button)));
        refs.price.textContent = variant.price;
        setSubmit();
      });
      refs.sizes.append(button);
      // Switching color keeps the size the shopper already picked, when that color has it in stock.
      if (keepLabel && variant.available && variant.label === keepLabel) button.click();
    });
    const available = variants.filter((variant) => variant.available);
    if (variants.length === 1 && available.length === 1) refs.sizes.querySelector('button')?.click();
    const single = variants.length === 1;
    refs.sizes.hidden = single;
    sizeLabel.hidden = single;
    const price = (chosen || available[0] || variants[0])?.price;
    if (price) refs.price.textContent = price;
    setSubmit();
  };

  const pickColor = (data, color) => {
    const keepLabel = chosen?.label;
    colorName.textContent = color;
    colorRow.querySelectorAll('[role="radio"]').forEach((button) => button.setAttribute('aria-checked', String(button.dataset.color === color)));
    const pictured = data.variants.find((variant) => variant.color === color && variant.image);
    refs.image.src = pictured ? pictured.image : data.image;
    renderSizes(data, color, keepLabel);
  };

  // Colors linked as separate products by the Fast Product Colors app: the group that contains this product.
  const linkedSwatches = (data) => {
    const groups = Array.isArray(data.linked) ? data.linked : [];
    const group = groups.find(
      (candidate) =>
        (candidate.type === 'colors' || /colou?r/i.test(candidate.option_name || '')) &&
        (candidate.swatches || []).some((swatch) => swatch.handle === data.handle)
    );
    return group && group.swatches.length > 1 ? group.swatches : null;
  };
  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  const sized = (src) => {
    if (!src) return '';
    const url = new URL(src, location.origin);
    url.searchParams.set('width', '1000');
    return url.toString();
  };
  // A linked product's /products/<handle>.js, in the same shape as the card's own data.
  const toData = (product) => {
    const sizeIndex = product.options.findIndex((option) => /size/i.test(option.name || option));
    return {
      productId: product.id,
      handle: product.handle,
      // Same rule as the theme: hide a " - Color" reporting suffix.
      title: product.title.replace(/\s+[-\u2013].*$/, ''),
      url: `${root}products/${product.handle}`,
      image: sized(product.featured_image),
      variants: product.variants.map((variant) => ({
        id: variant.id,
        label: sizeIndex >= 0 ? variant.options[sizeIndex] : product.variants.length === 1 ? 'One size' : variant.title,
        color: '',
        image: sized(variant.featured_image?.src),
        available: variant.available,
        price: money(variant.price),
      })),
    };
  };
  const cache = new Map();
  const loadLinked = (handle) => {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(`${root}products/${handle}.js`)
          .then((response) => {
            if (!response.ok) throw new Error('missing');
            return response.json();
          })
          .then(toData)
          .catch((error) => {
            cache.delete(handle);
            throw error;
          })
      );
    }
    return cache.get(handle);
  };
  const showProduct = (data, keepLabel) => {
    productId = data.productId;
    refs.image.src = data.image;
    refs.image.alt = data.title;
    refs.title.textContent = data.title;
    refs.link.href = data.url;
    renderSizes(data, '', keepLabel);
  };
  let linkToken = 0;
  const selectLinked = async (swatch) => {
    const keepLabel = chosen?.label;
    const token = ++linkToken;
    colorName.textContent = swatch.name;
    colorRow.querySelectorAll('[role="radio"]').forEach((button) => button.setAttribute('aria-checked', String(button.dataset.handle === swatch.handle)));
    refs.error.hidden = true;
    chosen = null;
    refs.submit.disabled = true;
    try {
      const data = await loadLinked(swatch.handle);
      if (token === linkToken) showProduct(data, keepLabel);
    } catch (error) {
      if (token !== linkToken) return;
      refs.error.textContent = 'This color could not be loaded. View full details to see it.';
      refs.error.hidden = false;
      refs.link.href = `${root}products/${swatch.handle}`;
    }
  };

  const open = (data) => {
    chosen = null;
    productId = data.productId;
    refs.image.src = data.image;
    refs.image.alt = data.title;
    refs.title.textContent = data.title;
    refs.link.href = data.url;
    refs.error.hidden = true;
    colorRow.innerHTML = '';
    const linked = linkedSwatches(data);
    if (linked) {
      linkToken += 1;
      linked.forEach((swatch) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sivana-quick-add__color';
        button.dataset.handle = swatch.handle;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-label', swatch.name);
        button.setAttribute('aria-checked', String(swatch.handle === data.handle));
        button.title = swatch.name;
        if (swatch.image) button.style.backgroundImage = `url("${swatch.image}")`;
        else if (swatch.color_two) button.style.background = `linear-gradient(135deg, ${swatch.color_one} 50%, ${swatch.color_two} 50%)`;
        else button.style.backgroundColor = swatch.color_one || swatch.name.toLowerCase();
        if (swatch.out_of_stock) button.classList.add('is-sold-out');
        button.addEventListener('click', () => {
          if (button.getAttribute('aria-checked') !== 'true') selectLinked(swatch);
        });
        colorRow.append(button);
      });
      colorName.textContent = linked.find((swatch) => swatch.handle === data.handle)?.name || '';
      colorsWrap.hidden = false;
      showProduct(data, null);
      dialog.showModal();
      return;
    }
    const colors = (data.colors || []).filter((color) => data.variants.some((variant) => variant.color === color.name));
    if (colors.length > 1) {
      colors.forEach((color) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sivana-quick-add__color';
        button.dataset.color = color.name;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-label', color.name);
        button.setAttribute('aria-checked', 'false');
        button.title = color.name;
        if (color.swatch) button.style.backgroundImage = `url("${color.swatch}")`;
        else button.style.backgroundColor = color.name.toLowerCase();
        if (!data.variants.some((variant) => variant.color === color.name && variant.available)) button.classList.add('is-sold-out');
        button.addEventListener('click', () => pickColor(data, color.name));
        colorRow.append(button);
      });
      colorsWrap.hidden = false;
      pickColor(data, data.startColor || colors[0].name);
    } else {
      colorsWrap.hidden = true;
      renderSizes(data, colors.length === 1 ? colors[0].name : '', null);
    }
    dialog.showModal();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-sivana-quick-add]');
    if (!trigger) return;
    try {
      open(JSON.parse(trigger.parentElement.querySelector('[data-quick-add-data]').textContent));
    } catch (error) {
      // Data missing: fall back to the product page.
      const link = trigger.parentElement.querySelector('a[href]');
      if (link) location.href = link.href;
    }
  });
  dialog.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  // The button animates like every Add to bag on the site (assets/sivana-atc.js: Adding with dots, then
  // Added with a check, leaves, count circle). It stays enabled-looking while it plays (busy blocks a
  // second tap), and the pop-up closes once the check has had a moment on screen.
  const ADDED_SEEN_MS = 900;
  const whenAdded = () =>
    new Promise((resolve) => {
      const done = () => {
        refs.submit.removeEventListener('sivana:atc-added', done);
        resolve();
      };
      refs.submit.addEventListener('sivana:atc-added', done);
      setTimeout(done, 2500); // in case the animation script isn't there
    });

  refs.submit.addEventListener('click', async () => {
    if (!chosen || busy) return;
    busy = true;
    refs.error.hidden = true;
    const added = whenAdded();
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    // Sent from inside this dialog: the cart drawer then waits for the dialog to close before opening.
    refs.submit.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(chosen.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: chosen.id, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.description || 'This size could not be added.');
      const cart = await (await fetch(`${root}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'sivana-quick-add', productId, itemCount: 1, sections: body.sections, didError: false },
      });
      await added;
      await new Promise((resolve) => setTimeout(resolve, ADDED_SEEN_MS));
      dialog.close();
    } catch (problem) {
      deferred.resolve({ detail: { didError: true } });
      refs.error.textContent = problem.message;
      refs.error.hidden = false;
    }
    busy = false;
  });
}

// "Pairs well with" pill: placed on the second photo (the first carries the model label) when the
// "Complete the look" row shows 2 pieces; clicking it scrolls down to the row.
const pillTemplate = document.querySelector('[data-sivana-pairs-pill]');
if (pillTemplate) {
  const row = pillTemplate.closest('.sivana-goes-with');
  const place = () => {
    const items = [
      ...document.querySelectorAll('.product-information__media .media-gallery__grid > li, .product-information__media slideshow-slide'),
    ].filter((item) => item.offsetWidth > 0);
    const target = items[1] || items[0];
    document.querySelectorAll('.sivana-pairs-pill').forEach((pill) => {
      if (pill.parentElement !== target) pill.remove();
    });
    if (!target || target.querySelector(':scope > .sivana-pairs-pill')) return;
    if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
    const pill = pillTemplate.content.firstElementChild.cloneNode(true);
    pill.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      row.scrollIntoView({ block: 'center' });
      row.classList.remove('is-highlighted');
      requestAnimationFrame(() => row.classList.add('is-highlighted'));
    });
    target.appendChild(pill);
  };
  place();
  document.addEventListener('shopify:product:select', () => setTimeout(place, 60));
}
